import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { useRoomStatus } from '../api/queries';
import { useRoomDetails, type RoomStay } from '../api/roomDetails';
import { ROOM_STATUS, formatDate, formatDateTime, formatGhs } from '../format';
import { Badge, Button, ErrorNote, Icon, Loading, useReport, useToast } from '../ui/kit';
import { StatTile, fmtInt, fmtPct } from '../ui/charts';
import { PageHead } from './shared';

const SOURCE: Record<string, string> = { FRONT_DESK: 'Front desk', PHONE: 'Phone', WHATSAPP: 'WhatsApp', WEBSITE: 'Website', WALK_IN: 'Walk-in' };
const BOOKING: Record<string, string> = { PENDING: 'Held', CONFIRMED: 'Confirmed', CHECKED_IN: 'In the house', CHECKED_OUT: 'Checked out', CANCELLED: 'Cancelled', NO_SHOW: 'No-show' };
const TASK: Record<string, string> = { CHECKOUT_CLEAN: 'Check-out clean', STAYOVER_CLEAN: 'Stay-over clean', DEEP_CLEAN: 'Deep clean' };
const TASK_STATUS: Record<string, string> = { OPEN: 'To do', IN_PROGRESS: 'Being cleaned', DONE: 'Done, to inspect', INSPECTED: 'Inspected', CANCELLED: 'Cancelled' };
const FAULT_STATUS: Record<string, string> = { OPEN: 'Open', IN_PROGRESS: 'Being fixed', RESOLVED: 'Fixed', CANCELLED: 'Cancelled' };
const PRIORITY: Record<string, string> = { NORMAL: 'Normal', HIGH: 'High', URGENT: 'Urgent' };
const plural = (n: number, one: string, many = `${one}s`): string => `${fmtInt(n)} ${n === 1 ? one : many}`;

/** One room: who is in it, who is coming, how it is kept, what is wrong with it, and how it has done. Reached by clicking a room. */
export function RoomDetail() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const q = useRoomDetails(id);
  const setStatus = useRoomStatus();
  const qc = useQueryClient();
  const toast = useToast();
  const report = useReport();
  const [busy, setBusy] = useState(false);

  if (q.isLoading) {
    return (
      <div className="page">
        <Loading />
      </div>
    );
  }
  if (q.error || !q.data) {
    return (
      <div className="page">
        <BackLink />
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      </div>
    );
  }

  const d = q.data;
  const r = d.room;
  const st = ROOM_STATUS[r.status] ?? { label: r.status, tone: '' as const };
  const editable = can('room.status.update') && r.status !== 'OCCUPIED' && r.status !== 'OUT_OF_ORDER';
  const change = (status: 'VACANT_CLEAN' | 'VACANT_DIRTY' | 'INSPECTED') => {
    setBusy(true);
    setStatus.mutate(
      { roomId: r.id, status, number: r.number },
      {
        onSuccess: () => {
          toast.ok(`Room ${r.number} marked ${ROOM_STATUS[status]?.label.toLowerCase()}`);
          void qc.invalidateQueries({ queryKey: ['rooms', 'details', r.id] });
        },
        onError: report,
        onSettled: () => setBusy(false),
      },
    );
  };

  return (
    <div className="page viz">
      <BackLink />
      <PageHead eyebrow={r.roomType.name} title={`Room ${r.number}`} />
      <div className="row" style={{ gap: 10 }}>
        <Badge tone={st.tone}>{st.label}</Badge>
        {r.floor ? <span className="small muted">{r.floor}</span> : null}
        {!r.active ? <Badge tone="bad">Not in use</Badge> : null}
        {!r.roomType.active ? <span className="small muted">This room type is switched off in Setup.</span> : null}
      </div>
      {editable ? (
        <div className="row">
          {r.status === 'VACANT_DIRTY' ? (
            <Button variant="primary" loading={busy} onClick={() => change('VACANT_CLEAN')}>
              Mark clean
            </Button>
          ) : (
            <Button loading={busy} onClick={() => change('VACANT_DIRTY')}>
              Mark dirty
            </Button>
          )}
          {r.status === 'VACANT_CLEAN' ? (
            <Button variant="ghost" loading={busy} onClick={() => change('INSPECTED')}>
              Inspected
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="viz-tiles">
        <StatTile label={`Occupancy, last ${d.lastDays.days} days`} value={fmtPct(d.lastDays.occupancyPct)} hint={`${plural(d.lastDays.nights, 'night')} with a guest`} />
        <StatTile label="Stays" value={fmtInt(d.lastDays.stays)} hint={`in the last ${d.lastDays.days} days`} />
        {d.can.bookings ? <StatTile label="Earned" value={formatGhs(d.lastDays.revenue)} hint="room charges, tax included" /> : null}
      </div>

      <div className="viz-cards">
        {d.can.bookings ? (
          <>
            <Card title="Right now">
              {d.current ? (
                <Stay s={d.current} lead={`In the room until ${formatDate(d.current.checkOut, true)}`} />
              ) : (
                <p className="viz-empty">{r.status === 'OUT_OF_ORDER' ? 'The room is out of order.' : 'Nobody is staying here today.'}</p>
              )}
            </Card>
            <Card title="Coming up" note="Bookings with this room assigned.">
              {d.upcoming.length ? d.upcoming.map((s) => <Stay key={s.id} s={s} lead={`Arrives ${formatDate(s.checkIn, true)}`} />) : <p className="viz-empty">No bookings are assigned to this room yet.</p>}
            </Card>
            <Card title="Recent stays">
              {d.recent.length ? d.recent.map((s) => <Stay key={s.id} s={s} lead={`Left ${formatDate(s.checkOut, true)}`} />) : <p className="viz-empty">No past stays.</p>}
            </Card>
          </>
        ) : (
          <Card title="Guests">
            <p className="viz-empty">Guest details are for reception and managers.</p>
          </Card>
        )}

        <Card title="About this room">
          <dl className="room-facts">
            <dt>Type</dt>
            <dd>{r.roomType.name}</dd>
            <dt>Sleeps</dt>
            <dd>
              {plural(r.roomType.maxAdults, 'adult')}
              {r.roomType.maxChildren ? `, ${plural(r.roomType.maxChildren, 'child', 'children')}` : ''}
            </dd>
            {r.floor ? (
              <>
                <dt>Where</dt>
                <dd>{r.floor}</dd>
              </>
            ) : null}
            {r.roomType.description ? (
              <>
                <dt>Includes</dt>
                <dd>{r.roomType.description}</dd>
              </>
            ) : null}
            {r.rates.map((p) => (
              <RateRow key={p.code} name={p.name} base={p.baseRate} weekend={p.weekendRate} tax={p.taxMode} />
            ))}
          </dl>
          {can('inventory.manage') || can('rates.manage') ? (
            <p className="viz-note">
              Change the room or its rates under <Link to="/setup">Setup</Link>.
            </p>
          ) : null}
        </Card>

        {d.cleaning ? (
          <Card title="Cleaning" note={d.cleaning.lastCleanedAt ? `Last cleaned ${formatDateTime(d.cleaning.lastCleanedAt)}.` : 'No cleaning has been recorded yet.'}>
            {d.cleaning.tasks.length ? (
              <ul className="room-list">
                {d.cleaning.tasks.map((t) => (
                  <li key={t.id}>
                    <span>{TASK[t.type] ?? t.type}</span>
                    <span className="muted">{formatDate(t.forDate)}</span>
                    <Badge tone={t.status === 'INSPECTED' || t.status === 'DONE' ? 'good' : t.status === 'CANCELLED' ? '' : 'info'}>{TASK_STATUS[t.status] ?? t.status}</Badge>
                  </li>
                ))}
              </ul>
            ) : null}
            {can('housekeeping.view') ? (
              <p className="viz-note">
                The whole day's list is on <Link to="/housekeeping">Housekeeping</Link>.
              </p>
            ) : null}
          </Card>
        ) : null}

        {d.faults ? (
          <Card title="Faults" note="Reported problems with this room.">
            {d.faults.length ? (
              <ul className="room-list">
                {d.faults.map((f) => (
                  <li key={f.id}>
                    <span>{f.title}</span>
                    <span className="muted">{formatDateTime(f.createdAt)}</span>
                    <span className="row" style={{ gap: 6 }}>
                      {f.priority !== 'NORMAL' ? <Badge tone={f.priority === 'URGENT' ? 'bad' : 'warn'}>{PRIORITY[f.priority] ?? f.priority}</Badge> : null}
                      <Badge tone={f.status === 'RESOLVED' ? 'good' : f.status === 'CANCELLED' ? '' : 'warn'}>{FAULT_STATUS[f.status] ?? f.status}</Badge>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="viz-empty">Nothing has been reported.</p>
            )}
          </Card>
        ) : null}

        {d.blocks.length ? (
          <Card title="Out of service" note="Dates this room cannot be sold.">
            <ul className="room-list">
              {d.blocks.map((b) => (
                <li key={b.id}>
                  <span>
                    {formatDate(b.from, true)} to {formatDate(b.to, true)}
                  </span>
                  <span className="muted">{b.reason}</span>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/rooms" className="row small" style={{ textDecoration: 'none' }}>
      <span style={{ width: 18, height: 18 }}>
        <Icon name="back" />
      </span>
      All rooms
    </Link>
  );
}

function Card({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className="viz-card" aria-label={title}>
      <header className="viz-card-head">
        <div>
          <h3 className="viz-title">{title}</h3>
          {note ? <p className="viz-note">{note}</p> : null}
        </div>
      </header>
      {children}
    </section>
  );
}

function Stay({ s, lead }: { s: RoomStay; lead: string }) {
  return (
    <Link to={`/bookings/${s.id}`} className="room-stay">
      <span className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
        <strong>{s.guest.name}</strong>
        <Badge tone={s.status === 'CHECKED_IN' ? 'info' : s.status === 'CHECKED_OUT' ? 'good' : ''}>{BOOKING[s.status] ?? s.status}</Badge>
      </span>
      <span className="small">{lead}</span>
      <span className="small muted">
        {formatDate(s.checkIn)} to {formatDate(s.checkOut)} · {plural(s.nights, 'night')} · {plural(s.adults, 'adult')}
        {s.children ? `, ${plural(s.children, 'child', 'children')}` : ''}
      </span>
      <span className="small muted">
        {s.reference} · {SOURCE[s.source] ?? s.source} · {formatGhs(s.total)}
        {s.guest.phone ? ` · ${s.guest.phone}` : ''}
      </span>
    </Link>
  );
}

function RateRow({ name, base, weekend, tax }: { name: string; base: number; weekend: number | null; tax: 'INCLUSIVE' | 'EXCLUSIVE' }) {
  return (
    <>
      <dt>{name}</dt>
      <dd>
        <span className="num">{formatGhs(base)}</span> a night{weekend !== null ? <>, <span className="num">{formatGhs(weekend)}</span> at weekends</> : null}
        <span className="muted"> · {tax === 'INCLUSIVE' ? 'tax included' : 'tax added on top'}</span>
      </dd>
    </>
  );
}
